import React, { useState, useEffect, useCallback } from 'react';
import { Container, Form, Button, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { FaEdit, FaEye, FaTrashAlt } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../Styles/yellowcalfcss/ExpensesDashboard.css';
import ApiConfig from '../../api/ApiConfig';
import { simplePostCall } from '../../api/ApiServices';
import ScaleLoader from 'react-spinners/ScaleLoader';
import { formatNumber } from '../../utils/formatNumber';

export default function ExpensesDashboard() {
  const navigate = useNavigate();
  
  // States
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Filters
  const [expenseNumberFilter, setExpenseNumberFilter] = useState('');
  const [accountTypeFilter, setAccountTypeFilter] = useState('');

  /**
   * 1. API Integration Function
   */
  const fetchExpensesList = useCallback(async () => {
    setLoading(true);
    const customer_id = localStorage.getItem("customer_id");
    const api_key = localStorage.getItem("api_key");
    const user_id = localStorage.getItem("user_id");

    const requestBody = {
      customer_id: Number(customer_id) || 1609,
      api_key: api_key || "8440d41adb8335314263dc31d4504cf7",
      user_id: Number(user_id) || 56950,
      language: "en",
      amount_search: "", 
      type_search: accountTypeFilter,  // Link to filter state
      number_search: expenseNumberFilter, // Link to filter state
      limit: 100,
      page: 1
    };

    try {
      const response = await simplePostCall(ApiConfig.expensesList, JSON.stringify(requestBody));

      if (response?.result) {
        // Update local state with API data
        setExpenses(response.data || []);
      } else {
        setExpenses([]);
      }
    } catch (error) {
      console.error("Error fetching expenses:", error);
      toast.error("Failed to load expenses list");
    } finally {
      setLoading(false);
    }
  }, [expenseNumberFilter, accountTypeFilter]); // Re-run when filters change

  // 2. Initial Fetch
  useEffect(() => {
    fetchExpensesList();
  }, [fetchExpensesList]);

  // 3. Delete Handler
const handleDelete = async (id) => {
    // 1. Confirm with user before deleting
    if (!window.confirm("Are you sure you want to delete this expense?")) return;

    setLoading(true);
    const customer_id = localStorage.getItem("customer_id");
    const api_key = localStorage.getItem("api_key");
    const user_id = localStorage.getItem("user_id");

    const requestBody = {
        customer_id: Number(customer_id) || 1609,
        api_key: api_key || "8440d41adb8335314263dc31d4504cf7",
        user_id: Number(user_id) || 56950,
        language: "en",
        id: id // The ID of the expense to delete
    };

    try {
        const response = await simplePostCall(ApiConfig.expensesListDelete, JSON.stringify(requestBody));

        if (response?.result) {
            toast.success("Expense deleted successfully");
            
            // 2. Refresh the list after successful deletion
            fetchExpensesList(); 
        } else {
            toast.error(response?.message || "Failed to delete expense");
        }
    } catch (error) {
        console.error("Error deleting expense:", error);
        toast.error("An error occurred during deletion");
    } finally {
        setLoading(false);
    }
};

  return (
    <div className="expdash-outer-wrap">
      <ToastContainer />

      {/* HEADER */}
      <div className="special-header panel-white-all">
        <h4 className="page-title">Expenses</h4>
        <div className="action-buttons-group">
          <Button className="btn btn-primary" onClick={() => navigate("/accountant/Type")}>
            Account Type
          </Button>
          <Button className="btn btn-primary" onClick={() => navigate("/accountant/ExpenseType")}>
            Expense Type
          </Button>
          <Button className="btn btn-primary" onClick={() => navigate("/accountant/createExpenses")}>
            Create Expense
          </Button>
          <Button className="btn btn-primary">
            Export
          </Button>
        </div>
      </div>

      <div className="yc-list-body">
        {/* FILTER */}
        <div className="yc-list-filter">
          <div className="ff-search-wrap">
            <Search size={14} className="ff-search-icon" />
            <Form.Control
              type="text"
              placeholder="Filter by Expense Number"
              value={expenseNumberFilter}
              onChange={(e) => setExpenseNumberFilter(e.target.value)}
              className="form-control ff-search-input"
            />
          </div>

          <div className="ff-search-wrap">
            <Search size={14} className="ff-search-icon" />
            <Form.Control
              type="text"
              placeholder="Filter by Account Type"
              value={accountTypeFilter}
              onChange={(e) => setAccountTypeFilter(e.target.value)}
              className="form-control ff-search-input"
            />
          </div>
        </div>

        {/* COLUMN HEADER */}
        <div className="yc-list-thead-card">
          <div className="yc-list-grid yc-list-grid-5">
            <div className="yc-list-cell">Sr.No</div>
            <div className="yc-list-cell">Expenses Number</div>
            <div className="yc-list-cell">Account Type</div>
            <div className="yc-list-cell">Total Amount</div>
            <div className="yc-list-cell">Actions</div>
          </div>
        </div>

        {/* ROWS */}
        {loading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ height: "20vh" }}>
            <ScaleLoader color="#F4BB44" />
          </div>
        ) : expenses.length > 0 ? (
          <div className="yc-list-row-list">
            {expenses.map((expense, index) => (
              <div key={expense.expenses_id} className="yc-list-row-card">
                <div className="yc-list-grid yc-list-grid-5">
                  <div className="yc-list-cell">{index + 1}</div>
                  <div className="yc-list-cell">{expense.expenses_number}</div>
                  <div className="yc-list-cell">{expense.expense_account_type_name}</div>
                  <div className="yc-list-cell">{formatNumber(expense.expenses_total_amount)}</div>
                  <div className="yc-list-cell yc-list-cell-actions">
                    <button className="yc-action-icon" onClick={() => navigate(`/ExpenseViewNew/${expense.expenses_id}`)} title="View">
                      <FaEye />
                    </button>
                    <button className="yc-action-icon" onClick={() => navigate(`/accountant/createExpenses/${expense.expenses_id}`)} title="Edit">
                      <FaEdit />
                    </button>
                    <button className="yc-action-icon yc-action-icon--danger" onClick={() => handleDelete(expense.expenses_id)} title="Delete">
                      <FaTrashAlt />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="yc-empty-state">No records found.</div>
        )}

        {/* SUMMARY */}
        <p className="expdash-summary-text">
          Showing {expenses.length} items
        </p>
      </div>
    </div>
  );
}